import React from 'react';
import '../../css/Chip.css';

// Tint a 3- or 6-digit hex color to an rgba string. Returns undefined for
// anything that isn't a valid hex so the chip falls back to its variant styling.
function tint(hex, alpha) {
    if (typeof hex !== 'string') return undefined;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return undefined;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Chip — unified tag / badge / pill.
 *
 * Props:
 *   variant   — 'neutral' | 'accent' | 'accent-subtle' | 'success' | 'warning' | 'danger'
 *   size      — 'sm' for the compact metadata-pill size
 *   color     — optional per-tag hex (e.g. '#4f8cff'); tints the chip when set
 *   onRemove  — show a × button when provided
 *   removeLabel — aria-label for the remove button
 */
const Chip = ({
    variant = 'neutral',
    size,
    color,
    onRemove,
    removeLabel = 'Remove',
    children,
    className = '',
    style,
    ...rest
}) => {
    const bg = tint(color, 0.16);
    const colorStyle = bg
        ? { backgroundColor: bg, borderColor: tint(color, 0.5), color: 'inherit', ...style }
        : style;

    return (
        <span
            className={`qd-chip qd-chip--${variant} ${size ? `qd-chip--${size}` : ''} ${className}`.trim()}
            style={colorStyle}
            {...rest}
        >
            <span className="qd-chip-label">{children}</span>
            {onRemove && (
                <button
                    type="button"
                    className="qd-chip-remove"
                    onClick={onRemove}
                    aria-label={removeLabel}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                         strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            )}
        </span>
    );
};

export default Chip;
